import React, { useEffect, useState } from "react";
import { Loader2, Plus, Pencil, Trash2, X, Save, Zap, Home as HomeIcon, ExternalLink, QrCode, Moon, Sparkles } from "lucide-react";
import axios from "axios";
import { fetchAdminRooms, adminCreateRoom, adminUpdateRoom, adminDeleteRoom, adminTestAction, adminDedupeRooms } from "../../lib/api";
import { toast } from "../../hooks/use-toast";
import TapparellePanel from "./TapparellePanel";
import LivoloLightsPanel from "./LivoloLightsPanel";
import HisenseACPanel from "./HisenseACPanel";
import FrameTVsPanel from "./FrameTVsPanel";
import QuickRequestsPanel from "./QuickRequestsPanel";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const domoticaClient = axios.create({ baseURL: `${BACKEND_URL}/api` });
domoticaClient.interceptors.request.use((cfg) => {
  const t = localStorage.getItem("admin_token");
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

function GoodnightButton() {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    if (!confirm("Chiudere TUTTE le tapparelle e spegnere TUTTE le luci Livolo di ogni camera?")) return;
    setBusy(true);
    try {
      const { data } = await domoticaClient.post("/domotica/routines/goodnight");
      const c = data.covers?.closed ?? 0;
      const l = data.lights?.turned_off ?? 0;
      const errs = (data.covers?.errors?.length || 0) + (data.lights?.errors?.length || 0);
      toast({
        title: `Buonanotte · ${c} tapparelle · ${l} luci spente`,
        description: errs > 0 ? `${errs} errori non bloccanti (vedi log)` : "Tutto ok, sogni d'oro 🌙",
      });
    } catch (e) {
      toast({ title: "Errore routine", description: e?.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  };
  return (
    <button
      onClick={run}
      disabled={busy}
      data-testid="goodnight-button"
      className="inline-flex items-center gap-2 rounded-full bg-gradient-to-br from-indigo-900 to-[#2b1410] hover:from-indigo-800 hover:to-[#4a1c17] text-[#f7ebdf] px-4 py-2 text-sm font-semibold shadow-md disabled:opacity-50"
      title="Chiude tutte le tapparelle e spegne tutte le luci Livolo"
    >
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Moon className="h-4 w-4" />}
      Buonanotte
    </button>
  );
}

// ---------------- Domotica / Rooms editor ----------------
function DedupeRoomsButton({ onDone }) {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    if (!window.confirm("Rimuovere le stanze duplicate? (i duplicati con lo stesso nome verranno eliminati, la più vecchia viene mantenuta)")) return;
    setBusy(true);
    try {
      const data = await adminDedupeRooms();
      toast({
        title: `${data.removed ?? 0} stanze duplicate rimosse`,
        description: `Totale stanze ora: ${data.total_rooms ?? "?"}`,
      });
      onDone && onDone();
    } catch (e) {
      toast({ title: "Errore dedupe", description: e?.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  };
  return (
    <button
      onClick={run}
      disabled={busy}
      data-testid="rooms-dedupe-btn"
      className="inline-flex items-center gap-1.5 rounded-full bg-white border border-[#e8dcc8] hover:bg-[#f7ebdf] text-[#4a1c17] px-3 py-1.5 text-xs font-medium disabled:opacity-50"
      title="Elimina le stanze duplicate mantenendo la più vecchia per ogni nome"
    >
      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
      Rimuovi duplicati
    </button>
  );
}

const ACTION_CATEGORIES_UI = [
  { key: "light", label: "Luci" },
  { key: "ac", label: "Clima" },
  { key: "blind", label: "Tapparelle" },
  { key: "tv", label: "TV" },
  { key: "scene", label: "Scenari" },
  { key: "other", label: "Altro" },
];
const ACTION_ICONS = ["lightbulb","lightbulb-off","snowflake","power","power-off","thermometer","arrow-up","arrow-down","tv","tv-off","wind"];

export default function RoomsEditor() {
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);   // room object being edited
  const [creating, setCreating] = useState(false);
  const [qrRoom, setQrRoom] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const res = await fetchAdminRooms(); setRooms(res.rooms || []); }
    catch (e) { toast({ title: "Errore", description: e?.message }); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (room) => {
    if (!window.confirm(`Eliminare la stanza "${room.name}"?`)) return;
    try { await adminDeleteRoom(room.id); toast({ title: "Stanza eliminata" }); load(); }
    catch (e) { toast({ title: "Errore", description: e?.response?.data?.detail || e?.message }); }
  };

  const publicBase = process.env.REACT_APP_BACKEND_URL || window.location.origin;

  return (
    <div>
      <div className="flex justify-end mb-3">
        <GoodnightButton />
      </div>
      <TapparellePanel />
      <div className="mt-4" />
      <LivoloLightsPanel />
      <div className="mt-4" />
      <HisenseACPanel />
      <div className="mt-4" />
      <FrameTVsPanel />
      <div className="mt-4" />
      <QuickRequestsPanel />
      <div className="flex flex-wrap items-center gap-3 mb-4 mt-6">
        <h2 className="font-serif text-2xl font-bold text-[#2b1410]">Domotica per stanza</h2>
        <DedupeRoomsButton onDone={load} />
        <button
          onClick={() => setCreating(true)}
          className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-[#4a1c17] hover:bg-[#2b1410] text-[#f7ebdf] px-4 py-2 text-sm font-medium"
          data-testid="room-add-btn"
        >
          <Plus className="h-4 w-4" /> Nuova stanza
        </button>
      </div>
      <p className="text-sm text-[#7c5a4e] mb-4">
        Ogni stanza ha un QR code che apre <code className="text-xs bg-white px-1.5 py-0.5 rounded border border-[#e8dcc8]">/room/&lt;slug&gt;</code>.
        L'ospite inserisce il PIN e attiva i comandi Alexa configurati (Voice Monkey webhook).
      </p>

      {loading ? (
        <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-[#4a1c17]" /></div>
      ) : rooms.length === 0 ? (
        <div className="rounded-lg bg-white border border-[#e8dcc8] p-8 text-center text-sm text-[#7c5a4e]">Nessuna stanza.</div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {rooms.map((r) => {
            const link = `${publicBase}/room/${r.slug}`;
            return (
              <div key={r.id} className="rounded-xl bg-white border border-[#e8dcc8] p-4 shadow-sm" data-testid={`room-card-${r.slug}`}>
                <div className="flex items-start gap-3">
                  <div className="h-10 w-10 rounded-full bg-[#4a1c17] text-[#f7ebdf] flex items-center justify-center shrink-0">
                    <HomeIcon className="h-5 w-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-serif text-lg font-bold text-[#2b1410] truncate">{r.name}</div>
                    <div className="text-xs text-[#7c5a4e]">slug: <code>{r.slug}</code> · PIN: <strong>{r.pin}</strong> · {r.actions?.length || 0} comandi</div>
                    <div className="mt-1 flex items-center gap-1 text-[11px]">
                      <a href={link} target="_blank" rel="noreferrer" className="text-[#4a1c17] hover:underline flex items-center gap-1 truncate">
                        <ExternalLink className="h-3 w-3" /> {link}
                      </a>
                      <button
                        onClick={() => { navigator.clipboard.writeText(link); toast({ title: "Link copiato" }); }}
                        className="ml-1 text-[#7c5a4e] hover:text-[#4a1c17]"
                        title="Copia link"
                      >
                        <QrCode className="h-3 w-3" />
                      </button>
                    </div>
                  </div>
                </div>
                <div className="mt-3 flex gap-2">
                  <button
                    onClick={() => setQrRoom(r)}
                    className="inline-flex items-center justify-center gap-1 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 text-xs font-medium"
                    data-testid={`room-qr-${r.slug}`}
                    title="Mostra QR"
                  >
                    <QrCode className="h-3.5 w-3.5" /> QR
                  </button>
                  <button
                    onClick={() => setEditing(r)}
                    className="flex-1 inline-flex items-center justify-center gap-1 rounded-md bg-[#4a1c17] hover:bg-[#2b1410] text-[#f7ebdf] px-3 py-1.5 text-xs font-medium"
                    data-testid={`room-edit-${r.slug}`}
                  >
                    <Pencil className="h-3.5 w-3.5" /> Modifica
                  </button>
                  <button
                    onClick={() => remove(r)}
                    className="inline-flex items-center justify-center gap-1 rounded-md bg-neutral-100 hover:bg-neutral-200 text-neutral-800 px-3 py-1.5 text-xs font-medium"
                    data-testid={`room-delete-${r.slug}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {(editing || creating) && (
        <RoomEditorModal
          room={editing}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSaved={() => { setEditing(null); setCreating(false); load(); }}
        />
      )}
      {qrRoom && (
        <RoomQRModal room={qrRoom} onClose={() => setQrRoom(null)} publicBase={publicBase} />
      )}
    </div>
  );
}

function RoomQRModal({ room, onClose, publicBase }) {
  const link = `${publicBase}/room/${room.slug}`;
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(link)}&size=400x400&margin=10&color=4a1c17&bgcolor=faf3ee`;
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="room-qr-modal">
      <div className="bg-white rounded-2xl max-w-sm w-full p-6 shadow-xl text-center" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-serif text-xl font-bold text-[#2b1410]">Stanza {room.name}</h3>
          <button onClick={onClose} className="p-1 hover:bg-neutral-100 rounded"><X className="h-4 w-4" /></button>
        </div>
        <img src={qrUrl} alt={`QR ${room.name}`} className="mx-auto rounded-lg border border-[#e8dcc8]" />
        <p className="mt-3 text-xs text-[#7c5a4e] break-all">{link}</p>
        <p className="mt-2 text-xs text-[#2b1410]"><strong>PIN:</strong> <span className="font-mono tracking-widest text-lg">{room.pin}</span></p>
        <div className="mt-4 flex justify-center gap-2">
          <button onClick={() => window.print()} className="rounded-full bg-[#4a1c17] hover:bg-[#2b1410] text-white px-4 py-2 text-sm">Stampa</button>
          <a href={qrUrl} download={`qr-${room.slug}.png`} className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 text-sm">Scarica PNG</a>
        </div>
      </div>
    </div>
  );
}

function RoomEditorModal({ room, onClose, onSaved }) {
  const isNew = !room;
  const [form, setForm] = useState(() => room ? {
    id: room.id,
    slug: room.slug,
    name: room.name,
    pin: room.pin,
    order: room.order || 0,
    actions: (room.actions || []).map((a, i) => ({ ...a, _key: a.id || `a-${i}` })),
  } : {
    slug: "", name: "", pin: "0000", order: 99, actions: [],
  });
  const [saving, setSaving] = useState(false);

  const addAction = () => {
    setForm((f) => ({
      ...f,
      actions: [
        ...f.actions,
        { _key: `n-${Date.now()}`, label: "Nuovo comando", icon: "power", category: "other", method: "GET", url: "", order: (f.actions.length + 1) },
      ],
    }));
  };
  const updateAction = (idx, patch) => {
    setForm((f) => ({ ...f, actions: f.actions.map((a, i) => i === idx ? { ...a, ...patch } : a) }));
  };
  const removeAction = (idx) => {
    setForm((f) => ({ ...f, actions: f.actions.filter((_, i) => i !== idx) }));
  };
  const testAction = async (idx) => {
    if (!form.id) { toast({ title: "Salva prima la stanza" }); return; }
    const a = form.actions[idx];
    if (!a.id) { toast({ title: "Salva prima le modifiche" }); return; }
    try {
      const res = await adminTestAction(form.id, a.id);
      toast({ title: res.ok ? "Comando inviato" : "Errore", description: `HTTP ${res.status}` });
    } catch (e) { toast({ title: "Errore", description: e?.response?.data?.detail || e?.message }); }
  };

  const save = async () => {
    if (!form.slug.trim() || !form.name.trim() || !form.pin.trim()) {
      toast({ title: "Slug, nome e PIN sono obbligatori" }); return;
    }
    setSaving(true);
    try {
      const payload = {
        slug: form.slug.trim().toLowerCase().replace(/[^a-z0-9-]/g, ""),
        name: form.name.trim(),
        pin: form.pin.trim(),
        order: Number(form.order) || 0,
        actions: form.actions.map(({ _key, ...a }, i) => ({
          ...a,
          order: i + 1,
          url: (a.url || "").trim(),
        })),
      };
      if (isNew) await adminCreateRoom(payload);
      else await adminUpdateRoom(form.id, payload);
      toast({ title: "Salvata" });
      onSaved();
    } catch (e) {
      toast({ title: "Errore", description: e?.response?.data?.detail || e?.message });
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-3" onClick={onClose} data-testid="room-editor-modal">
      <div className="bg-white rounded-2xl max-w-2xl w-full p-5 sm:p-6 shadow-xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-serif text-xl font-bold text-[#2b1410]">{isNew ? "Nuova stanza" : `Modifica: ${room.name}`}</h3>
          <button onClick={onClose} className="p-1 hover:bg-neutral-100 rounded"><X className="h-4 w-4" /></button>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
          <div className="sm:col-span-2">
            <label className="block text-xs uppercase tracking-wide font-semibold text-[#7c5a4e] mb-1">Nome</label>
            <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full rounded-md border border-[#e8dcc8] px-2 py-1.5 text-sm bg-white"
              data-testid="room-form-name" />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wide font-semibold text-[#7c5a4e] mb-1">Slug (URL)</label>
            <input type="text" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })}
              className="w-full rounded-md border border-[#e8dcc8] px-2 py-1.5 text-sm bg-white font-mono"
              data-testid="room-form-slug" />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wide font-semibold text-[#7c5a4e] mb-1">PIN</label>
            <input type="text" inputMode="numeric" value={form.pin} onChange={(e) => setForm({ ...form, pin: e.target.value.replace(/[^0-9]/g, "") })}
              className="w-full rounded-md border border-[#e8dcc8] px-2 py-1.5 text-sm bg-white font-mono tracking-widest"
              data-testid="room-form-pin" />
          </div>
        </div>

        <div className="mt-5 flex items-center gap-2 border-t border-[#e8dcc8] pt-4">
          <h4 className="font-serif text-base font-bold text-[#2b1410]">Comandi</h4>
          <button onClick={addAction} className="ml-auto inline-flex items-center gap-1 rounded-full bg-[#4a1c17] hover:bg-[#2b1410] text-[#f7ebdf] px-3 py-1 text-xs" data-testid="room-add-action">
            <Plus className="h-3 w-3" /> Aggiungi
          </button>
        </div>
        <div className="mt-2 space-y-2">
          {form.actions.map((a, idx) => (
            <div key={a._key || a.id} className="rounded-lg border border-[#e8dcc8] bg-[#faf3ee] p-3" data-testid={`room-action-row-${idx}`}>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 items-end">
                <div className="col-span-2 sm:col-span-2">
                  <label className="block text-[10px] uppercase font-semibold text-[#7c5a4e] mb-0.5">Etichetta</label>
                  <input type="text" value={a.label} onChange={(e) => updateAction(idx, { label: e.target.value })}
                    className="w-full rounded-md border border-[#e8dcc8] px-2 py-1 text-sm bg-white" />
                </div>
                <div>
                  <label className="block text-[10px] uppercase font-semibold text-[#7c5a4e] mb-0.5">Categoria</label>
                  <select value={a.category} onChange={(e) => updateAction(idx, { category: e.target.value })}
                    className="w-full rounded-md border border-[#e8dcc8] px-1 py-1 text-sm bg-white">
                    {ACTION_CATEGORIES_UI.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] uppercase font-semibold text-[#7c5a4e] mb-0.5">Icona</label>
                  <select value={a.icon} onChange={(e) => updateAction(idx, { icon: e.target.value })}
                    className="w-full rounded-md border border-[#e8dcc8] px-1 py-1 text-sm bg-white">
                    {ACTION_ICONS.map((k) => <option key={k} value={k}>{k}</option>)}
                  </select>
                </div>
                <div className="col-span-2 sm:col-span-3">
                  <label className="block text-[10px] uppercase font-semibold text-[#7c5a4e] mb-0.5">Webhook URL (Voice Monkey)</label>
                  <input type="url" value={a.url} onChange={(e) => updateAction(idx, { url: e.target.value })}
                    placeholder="https://api.voicemonkey.io/trigger?..."
                    className="w-full rounded-md border border-[#e8dcc8] px-2 py-1 text-xs font-mono bg-white" />
                </div>
                <div className="flex items-end gap-1">
                  <select value={a.method} onChange={(e) => updateAction(idx, { method: e.target.value })}
                    className="rounded-md border border-[#e8dcc8] px-1 py-1 text-xs bg-white">
                    <option value="GET">GET</option>
                    <option value="POST">POST</option>
                  </select>
                  <button onClick={() => testAction(idx)} title="Testa" className="rounded-md bg-emerald-600 hover:bg-emerald-700 text-white px-2 py-1 text-xs" data-testid={`room-test-action-${idx}`}>
                    <Zap className="h-3.5 w-3.5" />
                  </button>
                  <button onClick={() => removeAction(idx)} title="Elimina" className="rounded-md bg-neutral-200 hover:bg-red-100 text-neutral-800 px-2 py-1 text-xs">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
          {form.actions.length === 0 && (
            <div className="rounded-lg bg-white border border-dashed border-[#e8dcc8] p-6 text-center text-xs text-[#7c5a4e]">Nessun comando. Clicca "Aggiungi".</div>
          )}
        </div>

        <div className="mt-5 flex justify-end gap-2 border-t border-[#e8dcc8] pt-4">
          <button onClick={onClose} className="rounded-full bg-neutral-100 hover:bg-neutral-200 px-4 py-2 text-sm">Annulla</button>
          <button onClick={save} disabled={saving}
            className="rounded-full bg-[#4a1c17] hover:bg-[#2b1410] text-white px-4 py-2 text-sm font-medium disabled:opacity-60 inline-flex items-center gap-1.5"
            data-testid="room-save-btn"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Salva
          </button>
        </div>
      </div>
    </div>
  );
}
